import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchInsights } from '../api';
import { usePolling } from '../hooks/usePolling';
import type { Insights } from '../types';
import { STATUS_COLOR, STATUS_LABEL, STATUSES } from '../types';

function Card({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function Bar({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="mb-2">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-slate-700">{label}</span>
        <span className="text-slate-500">
          {count} <span className="text-slate-400">({pct.toFixed(0)}%)</span>
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function InsightsTab({
  active,
  reloadKey,
  role,
}: {
  active: boolean;
  reloadKey: number;
  role: string;
}) {
  const canViewTeam = role === 'admin' || role === 'manager' || role === 'super_admin';
  const [scope, setScope] = useState<'team' | 'me'>(canViewTeam ? 'team' : 'me');
  const [data, setData] = useState<Insights | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const loadedKeyRef = useRef<string | null>(null);
  const loadKey = useMemo(() => `${reloadKey}:${scope}`, [reloadKey, scope]);

  useEffect(() => {
    if (!canViewTeam && scope !== 'me') {
      setScope('me');
    }
  }, [canViewTeam, scope]);

  useEffect(() => {
    let alive = true;
    if (!active) return;
    if (loadedKeyRef.current === loadKey) return;
    setLoading(true);
    fetchInsights(scope)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setErr(null);
        loadedKeyRef.current = loadKey;
      })
      .catch((e) => alive && setErr(e instanceof Error ? e.message : 'load failed'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [active, loadKey, reloadKey, scope]);

  // Auto-poll every 60s — insights update less often, slower poll is fine.
  usePolling(
    async () => {
      const d = await fetchInsights(scope);
      setData(d);
      setErr(null);
      loadedKeyRef.current = loadKey;
    },
    60_000,
    [loadKey, scope],
    {
      enabled: active,
      immediate: false,
    }
  );

  const fallback: Insights = {
    scope: 'team',
    totals: { total_leads: 0, total_calls: 0, total_seconds: 0, unassigned: 0 },
    today: { new_leads: 0, calls_made: 0, wins_today: 0 },
    by_status: {},
    by_tier: {},
    by_source: {},
    by_campaign: {},
    conversion_rate: 0,
    top_telecallers: [],
  };
  const { totals, today, by_status, by_tier, by_source, by_campaign, conversion_rate, top_telecallers } =
    data ?? fallback;
  const totalLeads = totals.total_leads || 1;
  const totalSources = Object.values(by_source).reduce((a, b) => a + b, 0) || 1;
  const totalCampaigns = Object.values(by_campaign).reduce((a, b) => a + b, 0) || 1;
  const avgCall = totals.total_calls > 0 ? Math.round(totals.total_seconds / totals.total_calls) : 0;

  return (
    <div className="space-y-4 px-3 py-3">
      {canViewTeam ? (
        <div className="flex gap-1.5">
          {(['team', 'me'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium ${
                scope === s
                  ? 'bg-wechat-accent text-white'
                  : 'bg-white text-slate-600 border border-slate-200'
              }`}
            >
              {s === 'team' ? 'Team' : 'Me'}
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-center text-xs font-medium text-slate-600">
          Me
        </div>
      )}

      {loading && <p className="py-8 text-center text-sm text-slate-500">Loading insights…</p>}
      {err && <p className="rounded-lg bg-red-50 p-3 text-center text-sm text-red-700">{err}</p>}

      {!loading && !err && data && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Card label="Total leads" value={totals.total_leads} sub={scope === 'me' ? 'assigned to me' : undefined} />
            <Card label="New today" value={today.new_leads} />
            <Card label="Calls today" value={today.calls_made} />
            <Card
              label="Conversion"
              value={`${(conversion_rate * 100).toFixed(0)}%`}
              sub={`${by_status.won ?? 0} won / ${by_status.lost ?? 0} lost`}
            />
            <Card
              label="Wins today"
              value={today.wins_today}
              sub={`${today.wins_today > 0 ? '🔥' : ''}`}
            />
            <Card label="Unassigned" value={totals.unassigned} sub="needs pickup" />
          </div>

          <Card
            label="Avg call length"
            value={`${avgCall}s`}
            sub={`${totals.total_calls} calls, ${Math.round(totals.total_seconds / 60)}m total`}
          />

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Status breakdown
            </h3>
            {STATUSES.map((s) => (
              <Bar
                key={s}
                label={STATUS_LABEL[s]}
                count={by_status[s] ?? 0}
                total={totalLeads}
                color={STATUS_COLOR[s].replace(/text-\S+/, '').replace(/bg-(\w+)-100/, 'bg-$1-400')}
              />
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Tier mix
            </h3>
            <Bar label="Tier 1 — Hot" count={by_tier['1'] ?? 0} total={totalLeads} color="bg-red-400" />
            <Bar label="Tier 2 — Warm" count={by_tier['2'] ?? 0} total={totalLeads} color="bg-amber-400" />
            <Bar label="Tier 3 — Cold" count={by_tier['3'] ?? 0} total={totalLeads} color="bg-slate-400" />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Source attribution
            </h3>
            {Object.keys(by_source).length === 0 ? (
              <p className="text-xs text-slate-500">No data yet.</p>
            ) : (
              Object.entries(by_source)
                .sort((a, b) => b[1] - a[1])
                .map(([src, n]) => (
                  <Bar key={src} label={src} count={n} total={totalSources} color="bg-blue-400" />
                ))
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Campaigns (which Vobiz line / ad tag converts)
            </h3>
            {Object.keys(by_campaign).length === 0 ? (
              <p className="text-xs text-slate-500">No campaign data yet.</p>
            ) : (
              Object.entries(by_campaign)
                .sort((a, b) => b[1] - a[1])
                .map(([c, n]) => (
                  <Bar key={c} label={c} count={n} total={totalCampaigns} color="bg-indigo-400" />
                ))
            )}
          </div>

          {scope === 'team' && top_telecallers.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Top telecallers (active leads)
              </h3>
              <ul className="space-y-1.5">
                {top_telecallers.map((t) => (
                  <li
                    key={t.assigned_to}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-slate-700">{t.assigned_to}</span>
                    <span className="font-semibold text-slate-900">{t.n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
